import { createAnimation, getIonPageElement } from '@ionic/vue';
import type { Animation, TransitionOptions } from '@ionic/vue';

const DEFAULT_DURATION = 300;
// Ionic's own easings: a soft decelerate on enter, a sharper one on back.
const ENTER_EASING = 'cubic-bezier(0.36, 0.66, 0.04, 1)';
const BACK_EASING = 'cubic-bezier(0.47, 0, 0.745, 0.715)';

/**
 * App-wide default page transition.
 *
 * Replaces Ionic's default horizontal slide with a vertical "top to bottom"
 * motion: on forward navigation the entering page drops in from the top while
 * the leaving page slides down and out the bottom — the two pages tile and
 * move down together, reading as a top-to-bottom reveal. Back navigation
 * reverses the direction (new page rises from the bottom, old page exits up).
 *
 * Registered as `navAnimation` on the IonicVue config in `main.ts`, so every
 * `ion-router-outlet` page change uses it unless an explicit animation is
 * passed to the navigation call.
 */
export const topToBottomAnimation = (
  _baseEl: HTMLElement,
  opts: TransitionOptions,
): Animation => {
  const backDirection = opts.direction === 'back';

  const enterFrom = backDirection ? '100%' : '-100%';
  const leaveTo = backDirection ? '-100%' : '100%';

  const rootTransition = createAnimation()
    .duration(opts.duration ?? DEFAULT_DURATION)
    .easing(opts.easing ?? (backDirection ? BACK_EASING : ENTER_EASING));

  // Entering page slides in. `beforeRemoveClass('ion-page-invisible')` is
  // required so the page becomes visible at the start of the transition
  // instead of popping in only after it finishes.
  const enteringAnimation = createAnimation()
    .addElement(getIonPageElement(opts.enteringEl))
    .fill('both')
    .beforeRemoveClass('ion-page-invisible')
    .fromTo('transform', `translateY(${enterFrom})`, 'translateY(0)');

  rootTransition.addAnimation(enteringAnimation);

  // Leaving page slides out (absent on the very first page load).
  if (opts.leavingEl) {
    const leavingAnimation = createAnimation()
      .addElement(getIonPageElement(opts.leavingEl))
      .fill('both')
      .fromTo('transform', 'translateY(0)', `translateY(${leaveTo})`);

    rootTransition.addAnimation(leavingAnimation);
  }

  return rootTransition;
};

/**
 * Marker class a page sets on its root `<ion-page>` to opt into the
 * slide-from-right transition (see `appNavAnimation`). The app/info menu's
 * pages (About App, Help Center, Contact Support, Terms of Service, Privacy
 * Policy) use this so they slide in from the right edge — the classic forward
 * push — while the rest of the app keeps the default top-to-bottom motion.
 */
const SLIDE_RIGHT_CLASS = 'page-slide-right';

/**
 * Marker class for the mirror image: the page slides in from the LEFT edge,
 * travelling left to right. The Security PIN page (opened from the right-hand
 * account drawer) uses it.
 */
const SLIDE_LEFT_CLASS = 'page-slide-left';

/** How far the page underneath is pushed/dimmed during the slide (parallax). */
const PARALLAX_OFFSET = '22%';
const PARALLAX_OPACITY = '0.55';

/** True when the resolved ion-page element carries the given marker class. */
const pageHasClass = (el: HTMLElement | null | undefined, className: string): boolean => {
  if (!el) return false;
  const page = getIonPageElement(el) as HTMLElement | null;
  return !!page && page.classList.contains(className);
};

/** True when the resolved ion-page element opts into the slide-from-right motion. */
const isSlideRightEl = (el?: HTMLElement | null): boolean => pageHasClass(el, SLIDE_RIGHT_CLASS);

/** True when the resolved ion-page element opts into the slide-from-left motion. */
const isSlideLeftEl = (el?: HTMLElement | null): boolean => pageHasClass(el, SLIDE_LEFT_CLASS);

/**
 * Horizontal "panel" transition, shared by both slide directions.
 *
 * The panel page (the one carrying the marker class) slides in from `edge` and
 * covers the page beneath it; the page beneath parallaxes a little the other
 * way and dims. Back navigation reverses it: the panel slides back out to its
 * edge and the page beneath returns to rest. Direction is derived from which
 * element is the panel, so it reads correctly on forward and back navigation.
 */
const slidePanelAnimation = (
  opts: TransitionOptions,
  isPanel: (el?: HTMLElement | null) => boolean,
  edge: 'left' | 'right',
): Animation => {
  const backDirection = opts.direction === 'back';
  // Where the panel rests off-screen, and where the page beneath drifts to.
  const offscreen = edge === 'right' ? '100%' : '-100%';
  const parallax = edge === 'right' ? `-${PARALLAX_OFFSET}` : PARALLAX_OFFSET;

  const rootTransition = createAnimation()
    .duration(opts.duration ?? DEFAULT_DURATION)
    .easing(opts.easing ?? (backDirection ? BACK_EASING : ENTER_EASING));

  // ── Entering page ──────────────────────────────────────────────────
  const enteringAnimation = createAnimation()
    .addElement(getIonPageElement(opts.enteringEl))
    .fill('both')
    .beforeRemoveClass('ion-page-invisible');

  if (isPanel(opts.enteringEl)) {
    // The panel is entering (forward): slide in from its edge.
    enteringAnimation.fromTo('transform', `translateX(${offscreen})`, 'translateX(0)');
  } else {
    // The base page is entering (back): return from its parallax offset.
    enteringAnimation
      .fromTo('transform', `translateX(${parallax})`, 'translateX(0)')
      .fromTo('opacity', PARALLAX_OPACITY, '1');
  }
  rootTransition.addAnimation(enteringAnimation);

  // ── Leaving page ───────────────────────────────────────────────────
  if (opts.leavingEl) {
    const leavingAnimation = createAnimation()
      .addElement(getIonPageElement(opts.leavingEl))
      .fill('both');

    if (isPanel(opts.leavingEl)) {
      // The panel is leaving (back): slide back out to its edge.
      leavingAnimation.fromTo('transform', 'translateX(0)', `translateX(${offscreen})`);
    } else {
      // The base page is leaving (forward): parallax away from the panel and dim.
      leavingAnimation
        .fromTo('transform', 'translateX(0)', `translateX(${parallax})`)
        .fromTo('opacity', '1', PARALLAX_OPACITY);
    }
    rootTransition.addAnimation(leavingAnimation);
  }

  return rootTransition;
};

/**
 * Slide-from-right page transition (the classic iOS forward push): the panel
 * enters from the right edge, travelling right to left.
 */
export const slideFromRightAnimation = (
  _baseEl: HTMLElement,
  opts: TransitionOptions,
): Animation => slidePanelAnimation(opts, isSlideRightEl, 'right');

/**
 * Slide-from-left page transition, the mirror of `slideFromRightAnimation`:
 * the panel enters from the left edge, travelling left to right, and the page
 * beneath parallaxes to the right.
 */
export const slideFromLeftAnimation = (
  _baseEl: HTMLElement,
  opts: TransitionOptions,
): Animation => slidePanelAnimation(opts, isSlideLeftEl, 'left');

/**
 * App-wide navigation animation registered on `IonicVue` in `main.ts`.
 *
 * Dispatches per navigation: if either the entering or leaving page opts into
 * a horizontal slide (`SLIDE_RIGHT_CLASS` or `SLIDE_LEFT_CLASS`), use that
 * slide; otherwise fall back to the default `topToBottomAnimation`.
 */
export const appNavAnimation = (
  baseEl: HTMLElement,
  opts: TransitionOptions,
): Animation => {
  if (isSlideRightEl(opts.enteringEl) || isSlideRightEl(opts.leavingEl)) {
    return slideFromRightAnimation(baseEl, opts);
  }
  if (isSlideLeftEl(opts.enteringEl) || isSlideLeftEl(opts.leavingEl)) {
    return slideFromLeftAnimation(baseEl, opts);
  }
  return topToBottomAnimation(baseEl, opts);
};
